// BUILD-046 task 8.13 (supporting model): the Work groups page (design "Work groups page"; Decision D-N, RECORDED (Builder decision
// f041fa40), item 9; Req 6.1 - 6.3, 6.5, 6.17, 6.18, 14.20).
//
// A dedicated HQ page at /work-groups: list the Work_Groups, create one, add members and remove members, using the group routes. It shows
// NO Work_Item content: administering group membership and reading task content are separate authorities (Req 14.20), so this module's api
// has no task method at all and a test scans the page sources for task calls.
//
// Access: the nav item and this page are a convenience; the SERVER checks the Work_Group_Administrator authority on every group request
// (`admin.users.manage`, reused). The page asks `GET /work/me` (`canAdministerGroups`) first. Anyone else who opens the URL gets the
// NOT-AUTHORIZED state, a failure state and never an empty list, and no group route is called for them. A 403 from any group route moves the
// page to the same state. Rejected requests show the server's message and change nothing on screen.
//
// Every list (groups, members, employees) follows the list-state model: loading / error with its own Retry / empty / data, exclusive.
// Answers are tagged (Caller epoch, request number); a Caller change clears the page and starts again, and an answer for the previous Caller
// is discarded (Req 2.7).
//
// Framework-free controller (the page subscribes through useSyncExternalStore). Erasable TypeScript; `import type` plus the list-state model.

import { initialListState, listReducer, toListError, isStaleOutcome, describeList } from './listStateModel';
import type { ListState, ListView } from './listStateModel';
// D1 display cleanup: group names are SHOWN without the "(FICTIONAL)" test marker (stored names and ids are unchanged; a new name is created exactly as typed).
import { stripTestMarker } from './displayNames';

export interface GroupRef {
  groupId: string;
  name: string;
}

export interface GroupMember {
  employeeId: string;
  displayName: string;
  status: 'active' | 'inactive';
}

export interface EmployeeRef {
  employeeId: string;
  displayName: string;
}

export interface WorkGroupsApi {
  getMe: () => Promise<{ canAdministerGroups: boolean }>;
  listGroups: () => Promise<{ groups: readonly GroupRef[] }>;
  groupMembers: (groupId: string) => Promise<{ group: GroupRef; members: readonly GroupMember[] }>;
  listEmployees: () => Promise<{ employees: readonly EmployeeRef[] }>;
  createGroup: (name: string) => Promise<{ group: GroupRef }>;
  addGroupMembers: (groupId: string, employeeIds: string[]) => Promise<unknown>;
  removeGroupMembers: (groupId: string, employeeIds: string[]) => Promise<unknown>;
}

export const MAX_GROUP_NAME = 100;

export type AccessPhase = 'checking' | 'allowed' | 'denied' | 'error';

export interface AccessState {
  phase: AccessPhase;
  /** Why access could not be determined (phase `error`). */
  error: string | null;
}

export type Banner = { kind: 'ok' | 'error'; text: string } | null;

export interface WorkGroupsState {
  access: AccessState;
  groups: ListState<GroupRef>;
  employees: ListState<EmployeeRef>;
  members: ListState<GroupMember>;
  selectedGroupId: string | null;
  name: string;
  /** Employee IDs ticked in the "Add members" list. */
  toAdd: readonly string[];
  /** A create or a membership change is running: a second one is not sent. */
  saving: boolean;
  banner: Banner;
}

export function initialWorkGroupsState(): WorkGroupsState {
  return {
    access: { phase: 'checking', error: null },
    groups: initialListState<GroupRef>(),
    employees: initialListState<EmployeeRef>(),
    members: initialListState<GroupMember>(),
    selectedGroupId: null,
    name: '',
    toAdd: [],
    saving: false,
    banner: null,
  };
}

// ───────────────────────── pure helpers ─────────────────────────

/**
 * An ADVISORY hint for the name field. The server decides (Req 6.6: empty after trimming, longer than 100 characters, or the same as an
 * existing name when letter case is ignored): the page sends any non-empty name and shows the server's own message when it refuses.
 */
export function nameHint(name: string, existing: readonly string[]): string | null {
  const trimmed = name.trim();
  if (trimmed === '') return null;
  if (trimmed.length > MAX_GROUP_NAME) return `The name can be at most ${MAX_GROUP_NAME} characters.`;
  const lowered = trimmed.toLowerCase();
  if (existing.some((n) => n.trim().toLowerCase() === lowered)) return `A group named "${trimmed}" already exists (letter case is ignored).`;
  return null;
}

/** The people who can still be added: everyone listed except current members. Inactive members count as members (they are listed). */
export function addCandidates(employees: readonly EmployeeRef[], members: readonly GroupMember[]): EmployeeRef[] {
  const taken = new Set(members.map((m) => m.employeeId));
  return employees.filter((e) => !taken.has(e.employeeId));
}

function messageOf(thrown: unknown, fallback: string): string {
  if (thrown !== null && typeof thrown === 'object') {
    const t = thrown as { kind?: unknown; message?: unknown };
    if (t.kind === 'timeout') return 'There was no answer in time.';
    if (t.kind === 'network') return 'The server could not be reached.';
    if (typeof t.message === 'string' && t.message.trim() !== '') return t.message.trim();
  }
  return fallback;
}

function isForbidden(thrown: unknown): boolean {
  return thrown !== null && typeof thrown === 'object' && (thrown as { status?: unknown }).status === 403;
}

export const NOT_AUTHORIZED_TEXT = 'You are not authorized to manage work groups.';

export interface WorkGroupsView {
  /** Exactly one of these is the page body. */
  body: 'checking' | 'denied' | 'error' | 'ready';
  deniedText: string | null;
  accessError: string | null;
  groups: ListView<GroupRef>;
  members: ListView<GroupMember> | null;
  employees: ListView<EmployeeRef>;
  candidates: EmployeeRef[];
  selectedGroup: GroupRef | null;
  hint: string | null;
  canCreate: boolean;
  canAdd: boolean;
  saving: boolean;
  banner: Banner;
}

export function describeWorkGroups(state: WorkGroupsState): WorkGroupsView {
  const ready = state.access.phase === 'allowed';
  const groupItems = state.groups.items;
  const selected = state.selectedGroupId === null ? null : (groupItems.find((g) => g.groupId === state.selectedGroupId) ?? null);
  const memberItems = state.members.phase === 'data' ? state.members.items : [];
  const employeeItems = state.employees.phase === 'data' ? state.employees.items : [];
  return {
    body: state.access.phase === 'allowed' ? 'ready' : state.access.phase,
    deniedText: state.access.phase === 'denied' ? NOT_AUTHORIZED_TEXT : null,
    accessError: state.access.phase === 'error' ? (state.access.error ?? 'Your access could not be checked.') : null,
    groups: describeList(state.groups, 'work groups', 'There are no work groups yet.'),
    members: selected === null ? null : describeList(state.members, `members of ${stripTestMarker(selected.name) || selected.name}`, 'This group has no members yet.'),
    employees: describeList(state.employees, 'people', 'There are no people to add.'),
    candidates: addCandidates(employeeItems, memberItems),
    selectedGroup: selected,
    hint: nameHint(state.name, groupItems.map((g) => g.name)),
    canCreate: ready && !state.saving && state.name.trim() !== '',
    // A group with no members yet (phase 'empty') takes its first member: the list is loaded, it just holds nobody (found by the browser run, task 9.20).
    canAdd: ready && !state.saving && selected !== null && state.toAdd.length > 0 && (state.members.phase === 'data' || state.members.phase === 'empty'),
    saving: state.saving,
    banner: state.banner,
  };
}

// ───────────────────────── the controller ─────────────────────────

export interface WorkGroupsController {
  getState: () => WorkGroupsState;
  subscribe: (listener: () => void) => () => void;
  /** Checks access, then loads groups and people. Also the Retry of the access check. */
  start: () => Promise<void>;
  retryGroups: () => Promise<void>;
  retryEmployees: () => Promise<void>;
  retryMembers: () => Promise<void>;
  selectGroup: (groupId: string | null) => Promise<void>;
  setName: (name: string) => void;
  toggleToAdd: (employeeId: string) => void;
  createGroup: () => Promise<boolean>;
  addSelected: () => Promise<boolean>;
  removeMember: (employeeId: string) => Promise<boolean>;
  dismissBanner: () => void;
  /** A Caller change: the page is cleared and the access check starts again for the new Caller. */
  callerChanged: () => Promise<void>;
}

export function createWorkGroupsController(api: WorkGroupsApi, getEpoch: () => number): WorkGroupsController {
  let state = initialWorkGroupsState();
  const listeners = new Set<() => void>();
  const seqs = { access: 0, groups: 0, employees: 0, members: 0 };

  function publish(next: WorkGroupsState): void {
    if (next === state) return;
    state = next;
    for (const listener of [...listeners]) listener();
  }

  /** Runs one list request under the list-state model: request, fetch, apply only if it is still the latest of the latest epoch. */
  async function loadList<K extends 'groups' | 'employees' | 'members', T>(key: K, fetcher: () => Promise<readonly T[]>): Promise<readonly T[] | null> {
    const epoch = getEpoch();
    const seq = (seqs[key] += 1);
    const requested = listReducer(state[key] as ListState<T>, { type: 'request', epoch, seq });
    publish({ ...state, [key]: requested });
    try {
      const items = await fetcher();
      publish({ ...state, [key]: listReducer(state[key] as ListState<T>, { type: 'success', epoch, seq, items }) });
      return items;
    } catch (thrown) {
      if (isStaleOutcome(thrown)) return null;
      if (isForbidden(thrown)) {
        // The server says this Caller may not administer groups: the page becomes the not-authorized state and keeps no group data.
        if (epoch === getEpoch()) publish({ ...initialWorkGroupsState(), access: { phase: 'denied', error: null } });
        return null;
      }
      publish({ ...state, [key]: listReducer(state[key] as ListState<T>, { type: 'failure', epoch, seq, error: toListError(thrown) }) });
      return null;
    }
  }

  async function start(): Promise<void> {
    const epoch = getEpoch();
    const seq = (seqs.access += 1);
    publish({ ...initialWorkGroupsState(), access: { phase: 'checking', error: null } });
    let me: { canAdministerGroups: boolean };
    try {
      me = await api.getMe();
    } catch (thrown) {
      if (isStaleOutcome(thrown) || seq !== seqs.access || epoch !== getEpoch()) return;
      publish(isForbidden(thrown) ? { ...state, access: { phase: 'denied', error: null } } : { ...state, access: { phase: 'error', error: messageOf(thrown, 'Your access could not be checked.') } });
      return;
    }
    if (seq !== seqs.access || epoch !== getEpoch()) return;
    if (me.canAdministerGroups !== true) {
      publish({ ...state, access: { phase: 'denied', error: null } });
      return;
    }
    publish({ ...state, access: { phase: 'allowed', error: null } });
    await Promise.all([retryGroups(), retryEmployees()]);
  }

  async function retryGroups(): Promise<void> {
    await loadList('groups', async () => (await api.listGroups()).groups);
  }

  async function retryEmployees(): Promise<void> {
    await loadList('employees', async () => (await api.listEmployees()).employees);
  }

  async function retryMembers(): Promise<void> {
    const groupId = state.selectedGroupId;
    if (groupId === null) return;
    await loadList('members', async () => (await api.groupMembers(groupId)).members);
  }

  async function selectGroup(groupId: string | null): Promise<void> {
    if (state.access.phase !== 'allowed') return;
    publish({ ...state, selectedGroupId: groupId, members: initialListState<GroupMember>(), toAdd: [], banner: null });
    if (groupId !== null) await retryMembers();
  }

  /**
   * One create or membership change. A second one while it runs is not sent. `work` makes the request, reloads what changed and returns the
   * confirmation; `saving` stays on until the reload is done, so the page never offers a change on data it is about to replace.
   */
  async function mutate(work: () => Promise<Banner>, failureHeadline: string): Promise<boolean> {
    if (state.saving || state.access.phase !== 'allowed') return false;
    const epoch = getEpoch();
    publish({ ...state, saving: true, banner: null });
    try {
      const banner = await work();
      if (epoch !== getEpoch()) return false;
      publish({ ...state, saving: false, banner });
      return true;
    } catch (thrown) {
      if (isStaleOutcome(thrown) || epoch !== getEpoch()) return false;
      if (isForbidden(thrown)) {
        publish({ ...initialWorkGroupsState(), access: { phase: 'denied', error: null } });
        return false;
      }
      // Rejected: the server's message is shown and nothing on screen changes.
      publish({ ...state, saving: false, banner: { kind: 'error', text: `${failureHeadline} ${messageOf(thrown, '')}`.trim() } });
      return false;
    }
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start,
    retryGroups,
    retryEmployees,
    retryMembers,
    selectGroup,
    setName(name) {
      publish({ ...state, name });
    },
    toggleToAdd(employeeId) {
      const next = state.toAdd.includes(employeeId) ? state.toAdd.filter((id) => id !== employeeId) : [...state.toAdd, employeeId];
      publish({ ...state, toAdd: next });
    },
    async createGroup() {
      const name = state.name.trim();
      if (name === '') return false;
      return mutate(async () => {
        const created = await api.createGroup(name);
        publish({ ...state, name: '' });
        await retryGroups();
        await selectGroup(created.group.groupId);
        return { kind: 'ok', text: `Group "${stripTestMarker(created.group.name) || created.group.name}" created.` };
      }, 'The group was not created.');
    },
    async addSelected() {
      const groupId = state.selectedGroupId;
      const ids = [...state.toAdd];
      if (groupId === null || ids.length === 0) return false;
      return mutate(async () => {
        await api.addGroupMembers(groupId, ids);
        publish({ ...state, toAdd: [] });
        await retryMembers();
        return { kind: 'ok', text: ids.length === 1 ? 'Member added.' : `${ids.length} members added.` };
      }, 'The members were not added.');
    },
    async removeMember(employeeId) {
      const groupId = state.selectedGroupId;
      if (groupId === null) return false;
      return mutate(async () => {
        await api.removeGroupMembers(groupId, [employeeId]);
        await retryMembers();
        return { kind: 'ok', text: 'Member removed.' };
      }, 'The member was not removed.');
    },
    dismissBanner() {
      if (state.banner !== null) publish({ ...state, banner: null });
    },
    async callerChanged() {
      await start();
    },
  };
}
